import { Array, Option, Order, pipe, Result } from 'effect'

import type { RSheetColumn, RSheetRecordNode } from '../../column'
import type { CopyMatrix } from '../../copyMatrix'
import type { RSheetsCursor } from '../../cursor/cursorTypes'

export interface PasteTargetStructure {
  targetColumns: Array<RSheetColumn<RSheetRecordNode>>
  pseudoRootColumn: RSheetColumn<RSheetRecordNode>
  shouldRebuildCopyMatrix: boolean
}

interface PasteTargetStructureError {
  kind: 'CopyMatrixRowsInsufficient' | 'CopyMatrixWidthZero' | 'PasteTargetColumnNotFound' | 'PseudoRootColumnNotFound'
}

type GetPasteTargetStructureResult = Result.Result<PasteTargetStructure, PasteTargetStructureError>

// 貼り付け開始位置とコピー範囲から、貼り付け先として扱う列構造を決定する。
export const getPasteTargetStructure = ({
  copyMatrix,
  cursor,
  columns,
}: {
  copyMatrix: CopyMatrix
  cursor: RSheetsCursor
  columns: Array<RSheetColumn<RSheetRecordNode>>
}): GetPasteTargetStructureResult => {
  const [firstRow] = copyMatrix.rows
  if (firstRow === undefined || copyMatrix.rows.length === 0) {
    return Result.fail({ kind: 'CopyMatrixRowsInsufficient' })
  }

  const copyMatrixWidth = firstRow.columns.length
  if (copyMatrixWidth === 0) {
    return Result.fail({ kind: 'CopyMatrixWidthZero' })
  }

  const targetColumns = columns.slice(cursor.main.columnIndex, cursor.main.columnIndex + copyMatrixWidth)

  if (!Array.isArrayNonEmpty(targetColumns)) {
    return Result.fail({ kind: 'PasteTargetColumnNotFound' })
  }

  const pseudoRootColumn = getPseudoRootColumn({
    targetColumns,
    allColumns: columns,
  })
  if (pseudoRootColumn === undefined) {
    return Result.fail({ kind: 'PseudoRootColumnNotFound' })
  }

  const shouldRebuildCopyMatrix = targetColumns.every((column) =>
    isSamePath(column.node.path, pseudoRootColumn.node.path),
  )
  return Result.succeed({
    targetColumns,
    pseudoRootColumn,
    shouldRebuildCopyMatrix,
  })
}

// 2 つのパスが同じ階層構造を表しているかを判定する。
const isSamePath = (a: string[], b: string[]): boolean =>
  a.length === b.length &&
  pipe(
    a,
    Array.zip(b),
    Array.every(([aSegment, bSegment]) => aSegment === bSegment),
  )

// 貼り付け対象の列群から疑似ルート列を求め、全列の中に同一パスがあればその列を返す。
const getPseudoRootColumn = ({
  targetColumns,
  allColumns,
}: {
  targetColumns: Array.NonEmptyArray<RSheetColumn<RSheetRecordNode>>
  allColumns: Array<RSheetColumn<RSheetRecordNode>>
}): RSheetColumn<RSheetRecordNode> | undefined => {
  const shortestPathColumn = pipe(
    targetColumns,
    Array.min(Order.mapInput(Order.Number, (column) => column.node.path.length)),
  )
  const pseudoRootPath = shortestPathColumn.node.path

  return pipe(
    allColumns,
    Array.findFirst((column) => isSamePath(column.node.path, pseudoRootPath)),
    Option.getOrElse(() => shortestPathColumn),
  )
}
